import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api, fmt, fmtDate, fmtTimestamp, type Named, type User } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import Toast from '../components/Toast.tsx';
import { useList } from '../lib/useList.ts';

type Paper = 'KRAFT' | 'WHITE_BACK' | 'GREY_BACK';
type Kind = 'STOCK_IN' | 'TO_HALF' | 'HALF_USED' | 'FULL_USED' | 'COUNT_FIX' | 'UNDO';

interface Level {
  spec_id: number;
  paper: Paper;
  gsm: string;
  bf: string | null;
  size_in: string;
  size_value: string;
  size_unit: 'IN' | 'CM';
  name: string;
  full_reels: number;
  half_reels: number;
  last_stock_in: string | null;
}

interface Entry {
  entry_id: number;
  spec_id: number;
  reel_name: string;
  kind: Kind;
  full_change: number;
  half_change: number;
  entry_date: string;
  job_number: string | null;
  supplier_name: string | null;
  weight_kg: string | null;
  reason: string | null;
  entered_by_name: string;
  created_at: string;
  reverses_id: number | null;
  reversed_by_id: number | null;
}

interface Counts { full: number; half: number }

interface UndoPreview {
  allowed: boolean;
  blocked: string | null;
  reel: string;
  what: string;
  doneAt: string;
  doneBy: string;
  rows: { reel: string; what: string; now: Counts; after: Counts }[];
}

interface Held { text: string; undoUrl: string | null; key: number }

interface Move { toHalf: string; usedFull: string; usedHalf: string }

interface Day {
  day: string; stock_in: number; to_half: number; used_full: number; used_half: number;
  count_fixed: number; full_at_start: number; half_at_start: number; full_on_hand: number; half_on_hand: number;
}

const PAPER: Record<Paper, string> = { KRAFT: 'Kraft', WHITE_BACK: 'White back', GREY_BACK: 'Grey back' };

const KIND_LABEL: Record<Kind, string> = {
  STOCK_IN: 'Stock in', TO_HALF: 'Full → Half', HALF_USED: 'Half used',
  FULL_USED: 'Full used', COUNT_FIX: 'Count fixed', UNDO: 'Undo',
};

const trim = (v: string | number) => String(Number(v));
const unitWord = (u: 'IN' | 'CM') => (u === 'CM' ? 'cm' : 'in');
const sizeLabel = (l: Level) => `${trim(l.size_value)} ${unitWord(l.size_unit)}`;
const toInches = (v: number, u: 'IN' | 'CM') => (u === 'CM' ? v / 2.54 : v);

const groupTitle = (l: Level) => `${PAPER[l.paper]} ${trim(l.gsm)}${l.bf ? ` / ${trim(l.bf)}` : ''}`;
const groupKey = (l: Level) => `${l.paper}|${l.gsm}|${l.bf ?? ''}`;
const signed = (n: number) => (n > 0 ? `+${fmt(n)}` : n < 0 ? `−${fmt(Math.abs(n))}` : '—');
const reelsWord = (n: number) => `${n} reel${n === 1 ? '' : 's'}`;
const num = (v: string) => (v.trim() === '' ? 0 : /^\d+$/.test(v.trim()) ? Number(v.trim()) : Number.NaN);

function today() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}

function countLines(rows: UndoPreview['rows'], withReel: boolean) {
  return rows.flatMap((r) => {
    const parts: string[] = [];
    if (r.now.full !== r.after.full) parts.push(`Full ${r.now.full} → ${r.after.full}`);
    if (r.now.half !== r.after.half) parts.push(`Half ${r.now.half} → ${r.after.half}`);
    if (parts.length === 0) return [];
    return [withReel ? `${r.reel} · ${parts.join(' · ')}` : parts.join(' · ')];
  });
}

export default function ReelStock({ user, setError }: { user: User; setError: (s: string) => void }) {
  const canCreate = user.permissions.includes('stock_reels.create');
  const canUpdate = user.permissions.includes('stock_reels.update');

  const [tab, setTab] = useState<'hand' | 'history'>('hand');
  const [version, setVersion] = useState(0);
  const [held, setHeld] = useState<Held | null>(null);
  const [undoUrl, setUndoUrl] = useState<string | null>(null);

  const done = (text: string, url: string | null) => {
    setHeld({ text, undoUrl: url, key: Date.now() });
    setVersion((v) => v + 1);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 6, padding: '12px 16px 0' }} role="tablist">
        {([['hand', 'In hand'], ['history', 'History']] as const).map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key}
                  className={`btn${tab === key ? ' primary' : ''}`}
                  onClick={() => { setError(''); setTab(key); }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'hand' && <InHand canCreate={canCreate} version={version} setError={setError} onDone={done} />}
      {tab === 'history' && (
        <History key={version} canUpdate={canUpdate}
                 onUndo={(id) => setUndoUrl(`/api/stock/reel-entries/${id}/undo`)} />
      )}

      {held && (
        <Toast key={held.key} onClose={() => setHeld(null)}>
          <span>{held.text}</span>
          {canUpdate && held.undoUrl && (
            <button className="toast-act" onClick={() => { setUndoUrl(held.undoUrl); setHeld(null); }}>Undo</button>
          )}
        </Toast>
      )}
      {undoUrl && (
        <UndoWindow url={undoUrl} onClose={() => setUndoUrl(null)}
                    onDone={(text) => { setUndoUrl(null); done(text, null); }} />
      )}
    </>
  );
}

function InHand({ canCreate, version, setError, onDone }: {
  canCreate: boolean; version: number;
  setError: (s: string) => void; onDone: (text: string, undoUrl: string | null) => void;
}) {
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [paper, setPaper] = useState('');
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [moves, setMoves] = useState<Record<number, Move>>({});
  const [day, setDay] = useState(today());
  const [saving, setSaving] = useState(false);
  const [stocking, setStocking] = useState(false);
  const [looking, setLooking] = useState<Level | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ levels: Level[] }>('/api/stock/reel-levels?includeEmpty=true');
      setLevels(res.levels);
    } catch (err) {
      setError(errorText(err, 'Could not load reels'));
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => { void load(); }, [load, version]);

  const rows = useMemo(() => {
    const needle = search.replace(/\s+/g, '').toLowerCase();
    return levels.filter((l) =>
      (!paper || l.paper === paper)
      && (!needle || l.name.replace(/\s+/g, '').toLowerCase().includes(needle)));
  }, [levels, search, paper]);

  const outcome = useCallback((l: Level) => {
    const m = moves[l.spec_id];
    const toHalf = m ? num(m.toHalf) : 0;
    const usedFull = m ? num(m.usedFull) : 0;
    const usedHalf = m ? num(m.usedHalf) : 0;
    if ([toHalf, usedFull, usedHalf].some(Number.isNaN)) {
      return { full: l.full_reels, half: l.half_reels, toHalf: 0, usedFull: 0, usedHalf: 0, problem: 'Whole numbers only' };
    }
    const full = l.full_reels - toHalf - usedFull;
    const half = l.half_reels + toHalf - usedHalf;
    const problem =
      full < 0 ? `Only ${l.full_reels} Full in hand`
      : half < 0 ? `Only ${l.half_reels + toHalf} Half in hand`
      : '';
    return { full: problem ? l.full_reels : full, half: problem ? l.half_reels : half, toHalf, usedFull, usedHalf, problem };
  }, [moves]);

  const groups = useMemo(() => {
    const map = new Map<string, { key: string; title: string; rows: Level[]; full: number; half: number }>();
    for (const l of rows) {
      const key = groupKey(l);
      const g = map.get(key) ?? { key, title: groupTitle(l), rows: [], full: 0, half: 0 };
      const o = outcome(l);
      g.rows.push(l);
      g.full += o.full;
      g.half += o.half;
      map.set(key, g);
    }
    return [...map.values()];
  }, [rows, outcome]);

  const totals = useMemo(() => ({
    full: groups.reduce((n, g) => n + g.full, 0),
    half: groups.reduce((n, g) => n + g.half, 0),
  }), [groups]);

  const overall = useMemo(() => ({
    full: levels.reduce((n, l) => n + l.full_reels, 0),
    half: levels.reduce((n, l) => n + l.half_reels, 0),
  }), [levels]);

  const edits = useMemo(() => {
    const changes: { level: Level; toHalf: number; usedFull: number; usedHalf: number }[] = [];
    let invalid = false;
    for (const l of levels) {
      if (!moves[l.spec_id]) continue;
      const o = outcome(l);
      if (o.problem) { invalid = true; continue; }
      if (o.toHalf + o.usedFull + o.usedHalf > 0) {
        changes.push({ level: l, toHalf: o.toHalf, usedFull: o.usedFull, usedHalf: o.usedHalf });
      }
    }
    return { changes, invalid };
  }, [levels, moves, outcome]);

  const allClosed = groups.length > 0 && groups.every((g) => closed.has(g.key));

  const toggle = (key: string) => setClosed((s) => {
    const next = new Set(s);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  function setField(l: Level, field: keyof Move, value: string) {
    setMoves((m) => ({ ...m, [l.spec_id]: { ...(m[l.spec_id] ?? { toHalf: '', usedFull: '', usedHalf: '' }), [field]: value } }));
  }

  function stopEditing() {
    setEditing(false);
    setMoves({});
    setDay(today());
  }

  function nextDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const col = e.currentTarget.dataset.col;
    const same = [...(gridRef.current?.querySelectorAll<HTMLInputElement>(`input[data-col="${col}"]`) ?? [])];
    same[same.indexOf(e.currentTarget) + 1]?.focus();
  }

  async function save() {
    if (edits.invalid || edits.changes.length === 0) return;
    setSaving(true);
    try {
      const res = await api.post<{ batchId: string; saved: number }>('/api/stock/reel-moves', {
        moves: edits.changes.map((c) => ({
          specId: c.level.spec_id, toHalf: c.toHalf, usedFull: c.usedFull, usedHalf: c.usedHalf,
        })),
        ...(day !== today() ? { entryDate: day } : {}),
      });
      stopEditing();
      onDone(`Saved · ${res.saved} size${res.saved === 1 ? '' : 's'}`, `/api/stock/reel-batches/${res.batchId}/undo`);
    } catch (err) {
      setError(errorText(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  }

  const lines = edits.changes.map((c) => {
    const parts: string[] = [];
    if (c.toHalf) parts.push(`${c.toHalf} Full → Half`);
    if (c.usedFull) parts.push(`${c.usedFull} Full used`);
    if (c.usedHalf) parts.push(`${c.usedHalf} Half used`);
    return `${c.level.name} · ${parts.join(' · ')}`;
  });
  const shown = lines.slice(0, 4);
  const cols = editing ? 6 : 3;

  const cell = (l: Level, field: keyof Move, label: string) => {
    const m = moves[l.spec_id];
    const v = m?.[field] ?? '';
    const bad = m && outcome(l).problem !== '';
    const set = v.trim() !== '' && v.trim() !== '0';
    return (
      <td className="num">
        <input className={`reel-count${bad ? ' bad' : set ? ' changed' : ''}`} type="number" min="0" step="1"
               inputMode="numeric" data-col={field} aria-label={`${label}, ${l.name}`} value={v}
               title={bad ? outcome(l).problem : undefined}
               onFocus={(e) => e.currentTarget.select()} onKeyDown={nextDown}
               onChange={(e) => setField(l, field, e.target.value)} />
      </td>
    );
  };

  return (
    <div className="reel-card">
      <div className="pp-toolbar">
        <span className="reel-sum">
          {loading ? 'Loading…' : <><strong>{fmt(overall.full)}</strong> Full<i>·</i><strong>{fmt(overall.half)}</strong> Half</>}
        </span>
        <span className="spacer" />
        <input className="search" placeholder="Search size or 120/14" value={search}
               onChange={(e) => setSearch(e.target.value)} />
        <select className="pp-select" aria-label="Paper" value={paper} onChange={(e) => setPaper(e.target.value)}>
          <option value="">All paper</option>
          {(Object.keys(PAPER) as Paper[]).map((p) => <option key={p} value={p}>{PAPER[p]}</option>)}
        </select>
        <button className="btn" disabled={groups.length === 0}
                onClick={() => setClosed(allClosed ? new Set() : new Set(groups.map((g) => g.key)))}>
          {allClosed ? 'Expand all' : 'Collapse all'}
        </button>
        {canCreate && !editing && (
          <>
            <button className="btn" onClick={() => setEditing(true)}>Update today</button>
            <button className="btn primary" onClick={() => setStocking(true)}>Stock in</button>
          </>
        )}
      </div>

      {!loading && levels.length === 0 ? (
        <div className="empty">No reels yet. Click Stock in to record the first delivery.</div>
      ) : !loading && groups.length === 0 ? (
        <div className="empty">No reel matches that.</div>
      ) : (
        <div className="reel-wrap" ref={gridRef}>
            <table className="reel-grid">
              <colgroup>
                <col />
                <col style={{ width: 90 }} /><col style={{ width: 90 }} />
                {editing && <><col style={{ width: 110 }} /><col style={{ width: 110 }} /><col style={{ width: 110 }} /></>}
              </colgroup>
              <thead>
                <tr>
                  <th>Type / Size</th>
                  <th className="num">Full</th>
                  <th className="num">Half</th>
                  {editing && <><th className="num">Full → Half</th><th className="num">Full used</th><th className="num">Half used</th></>}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const open = !closed.has(g.key);
                  return (
                    <Fragment key={g.key}>
                      <tr className="reel-type" onClick={() => toggle(g.key)}>
                        <td>
                          <button className="reel-twirl" aria-expanded={open} aria-label={`${g.title} sizes`}
                                  onClick={(e) => { e.stopPropagation(); toggle(g.key); }}>
                            {open ? '▾' : '▸'}
                          </button>
                          {g.title}
                          <span className="dim" style={{ fontWeight: 400 }}> · {g.rows.length} size{g.rows.length === 1 ? '' : 's'}</span>
                        </td>
                        <td className="num">{fmt(g.full)}</td>
                        <td className="num">{fmt(g.half)}</td>
                        {editing && <td colSpan={3} />}
                      </tr>
                      {open && g.rows.map((l) => {
                        const o = outcome(l);
                        const changed = editing && (o.full !== l.full_reels || o.half !== l.half_reels);
                        return (
                          <tr key={l.spec_id} className="reel-size">
                            <td>
                              <button className="reel-link" onClick={() => setLooking(l)}>{sizeLabel(l)}</button>
                            </td>
                            <td className={`num${changed && o.full !== l.full_reels ? ' reel-new' : ''}`}>{fmt(o.full)}</td>
                            <td className={`num${changed && o.half !== l.half_reels ? ' reel-new' : ''}`}>{fmt(o.half)}</td>
                            {editing && (
                              <>
                                {cell(l, 'toHalf', 'Full to Half')}
                                {cell(l, 'usedFull', 'Full used')}
                                {cell(l, 'usedHalf', 'Half used')}
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="reel-total">
                  <td>Total</td>
                  <td className="num">{fmt(totals.full)}</td>
                  <td className="num">{fmt(totals.half)}</td>
                  {editing && <td colSpan={cols - 3} />}
                </tr>
              </tfoot>
            </table>
        </div>
      )}

      {editing && (
        <div className="reel-bar">
          <div className="reel-bar-info">
            {lines.length === 0 ? (
              <span className="dim">{edits.invalid ? 'Check the highlighted rows' : 'Nothing to save yet'}</span>
            ) : (
              <>
                <strong>{lines.length} size{lines.length === 1 ? '' : 's'} to save</strong>
                <ul>
                  {shown.map((l) => <li key={l}>{l}</li>)}
                  {lines.length > shown.length && <li className="dim">and {lines.length - shown.length} more</li>}
                </ul>
              </>
            )}
          </div>
          <label className="dim reel-day">
            Day
            <input type="date" value={day} max={today()} onChange={(e) => setDay(e.target.value || today())} />
          </label>
          <button className="btn" onClick={stopEditing} disabled={saving}>Cancel</button>
          <button className="btn primary" onClick={() => void save()}
                  disabled={saving || edits.invalid || lines.length === 0}>
            {saving ? 'Saving…' : lines.length === 0 ? 'Save' : `Save ${lines.length} size${lines.length === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {stocking && (
        <StockInWindow levels={levels} onClose={() => setStocking(false)}
                       onDone={(text, entryId) => {
                         setStocking(false);
                         onDone(text, `/api/stock/reel-entries/${entryId}/undo`);
                       }} />
      )}
      {looking && <SizeHistory level={looking} onClose={() => setLooking(null)} />}
    </div>
  );
}

function SizeHistory({ level, onClose }: { level: Level; onClose: () => void }) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<{ entries: Entry[] }>(
      `/api/stock/reel-entries?page=1&pageSize=50&sort=created_at&dir=desc&filter.spec_id=${level.spec_id}`)
      .then((r) => setEntries(r.entries)).catch((err) => setError(errorText(err, 'Could not load')));
  }, [level.spec_id]);

  return (
    <Window title="History" sub={level.name} width={560} error={error} onClose={onClose}
            foot={<button className="btn primary" onClick={onClose}>Close</button>}>
      {entries === null && !error && <div className="dim">Loading…</div>}
      {entries?.length === 0 && <div className="dim">Nothing recorded for this size yet.</div>}
      {entries && entries.length > 0 && (
        <table className="reel-grid">
          <colgroup><col /><col style={{ width: 130 }} /><col style={{ width: 60 }} /><col style={{ width: 60 }} /><col style={{ width: 90 }} /></colgroup>
          <thead>
            <tr><th>When</th><th>Entry</th><th className="num">Full</th><th className="num">Half</th><th>By</th></tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.entry_id} className={e.reversed_by_id ? 'struck' : undefined}>
                <td className="dim">{fmtTimestamp(e.created_at)}</td>
                <td><strong>{KIND_LABEL[e.kind]}</strong></td>
                <td className="num">{signed(e.full_change)}</td>
                <td className="num">{signed(e.half_change)}</td>
                <td className="dim">{e.entered_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Window>
  );
}

function Changes({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <ul style={{ margin: '4px 0 14px', paddingLeft: 18, fontSize: 13.5 }}>
      {lines.map((l) => <li key={l}>{l}</li>)}
    </ul>
  );
}

function Window({ title, sub, width = 460, error, children, foot, onClose }: {
  title: string; sub?: string; width?: number; error?: string;
  children: ReactNode; foot: ReactNode; onClose: () => void;
}) {
  return (
    <div className="backdrop" style={{ zIndex: 30 }} onClick={onClose}>
      <div className="modal" style={{ width }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          {sub && <p>{sub}</p>}
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          {children}
        </div>
        <div className="modal-foot">{foot}</div>
      </div>
    </div>
  );
}

function StockInWindow({ levels, onClose, onDone }: {
  levels: Level[]; onClose: () => void; onDone: (text: string, entryId: number, level: Level) => void;
}) {
  const [paper, setPaper] = useState<Paper>('KRAFT');
  const [gsm, setGsm] = useState('');
  const [bf, setBf] = useState('');
  const [size, setSize] = useState('');
  const [unit, setUnit] = useState<'IN' | 'CM'>('IN');
  const [sameChoice, setSameChoice] = useState<'existing' | 'new' | ''>('');
  const [reels, setReels] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [date, setDate] = useState(today());
  const [weight, setWeight] = useState('');
  const [suppliers, setSuppliers] = useState<Named[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ suppliers: Named[] }>('/api/reference/suppliers')
      .then((r) => setSuppliers(r.suppliers)).catch(() => {});
  }, []);

  const n = Number(reels);
  const sameSpec = (l: Level) =>
    l.paper === paper && Number(l.gsm) === Number(gsm) && Number(l.bf ?? 0) === Number(bf || 0);
  const existing = levels.find((l) => sameSpec(l) && Number(l.size_value) === Number(size) && l.size_unit === unit);
  const typedInches = Number(size) > 0 ? toInches(Number(size), unit) : 0;
  const otherUnit = !existing && typedInches > 0
    ? levels.find((l) => sameSpec(l) && l.size_unit !== unit && Math.abs(Number(l.size_in) - typedInches) <= 0.04)
    : undefined;
  const target = otherUnit && sameChoice === 'existing' ? otherUnit : existing;

  const problem =
    !(Number(gsm) > 0) ? 'Enter the GSM'
    : paper === 'KRAFT' && !(Number(bf) > 0) ? 'Enter the BF'
    : !(Number(size) > 0) ? 'Enter the size'
    : !Number.isInteger(n) || n <= 0 ? 'Enter the number of reels'
    : otherUnit && !sameChoice ? 'Choose where to add these reels'
    : '';

  const name = !problem
    ? `${PAPER[paper]} ${trim(gsm)}${Number(bf) > 0 ? `/${trim(bf)}` : ''} · ${trim(size)} ${unitWord(unit)}`
    : '';
  const lines = !problem
    ? [target ? `Full ${target.full_reels} → ${target.full_reels + n}` : `Adds ${n} Full · new size in the list`]
    : [];

  async function submit() {
    if (problem) return;
    setBusy(true); setError('');
    try {
      const res = await api.post<{ entryId: number; level: Level }>('/api/stock/reel-levels', {
        paper, gsm: Number(gsm), reels: n, entryDate: date,
        ...(target && target !== existing
          ? { size: Number(target.size_value), unit: target.size_unit }
          : { size: Number(size), unit }),
        ...(Number(bf) > 0 ? { bf: Number(bf) } : {}),
        ...(supplierId ? { supplierId: Number(supplierId) } : {}),
        ...(Number(weight) > 0 ? { weightKg: Number(weight) } : {}),
      });
      onDone(`Stocked in ${reelsWord(n)} · ${res.level.name}`, res.entryId, res.level);
    } catch (err) {
      setError(errorText(err, 'Could not stock in'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Window title="Stock in reels" sub={name || undefined} error={error} onClose={onClose}
            foot={<>
              {problem && <span className="hint" style={{ marginRight: 'auto' }}>{problem}</span>}
              <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn primary" disabled={busy || !!problem} onClick={() => void submit()}>
                {busy ? 'Saving…' : `Stock in ${n > 0 ? reelsWord(n) : 'reels'}`}
              </button>
            </>}>
      <div className="field">
        <label htmlFor="ri-paper" className="req">Paper</label>
        <select id="ri-paper" value={paper} onChange={(e) => setPaper(e.target.value as Paper)}>
          {(Object.keys(PAPER) as Paper[]).map((p) => <option key={p} value={p}>{PAPER[p]}</option>)}
        </select>
      </div>
      <div className="row2">
        <div className="field">
          <label htmlFor="ri-gsm" className="req">GSM</label>
          <input id="ri-gsm" type="number" min="1" value={gsm} autoFocus onChange={(e) => setGsm(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ri-bf" className={paper === 'KRAFT' ? 'req' : undefined}>BF</label>
          <input id="ri-bf" type="number" min="1" value={bf} onChange={(e) => setBf(e.target.value)} />
        </div>
      </div>
      <div className="row2">
        <div className="field">
          <label htmlFor="ri-size" className="req">Size</label>
          <div className="reel-size">
            <input id="ri-size" type="number" min="1" step="0.25" value={size}
                   onChange={(e) => { setSize(e.target.value); setSameChoice(''); }} />
            <select aria-label="Size unit" value={unit}
                    onChange={(e) => { setUnit(e.target.value as 'IN' | 'CM'); setSameChoice(''); }}>
              <option value="IN">in</option>
              <option value="CM">cm</option>
            </select>
          </div>
          {typedInches > 0 && (
            <div className="hint">
              {unit === 'CM'
                ? `${trim(size)} cm ≈ ${Number(typedInches.toFixed(2))} in`
                : `${trim(size)} in ≈ ${Number((Number(size) * 2.54).toFixed(1))} cm`}
            </div>
          )}
        </div>
        <div className="field">
          <label htmlFor="ri-n" className="req">Full reels</label>
          <input id="ri-n" type="number" min="1" step="1" value={reels} onChange={(e) => setReels(e.target.value)} />
        </div>
      </div>
      <div className="row2">
        <div className="field">
          <label htmlFor="ri-sup">Supplier</label>
          <select id="ri-sup" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">—</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="ri-date">Date</label>
          <input id="ri-date" type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>
      <div className="field" style={{ marginBottom: 8 }}>
        <label htmlFor="ri-weight">Weight per reel (kg)</label>
        <input id="ri-weight" type="number" min="0" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} />
      </div>
      {otherUnit && (
        <div className="reel-same" role="group" aria-label="Same size already in stock">
          <div>Already in stock as <strong>{sizeLabel(otherUnit)}</strong> ({otherUnit.full_reels} Full)</div>
          <div className="reel-same-pick">
            <button type="button" className={sameChoice === 'existing' ? 'on' : undefined}
                    onClick={() => setSameChoice('existing')}>Add to {sizeLabel(otherUnit)}</button>
            <button type="button" className={sameChoice === 'new' ? 'on' : undefined}
                    onClick={() => setSameChoice('new')}>New row in {unitWord(unit)}</button>
          </div>
        </div>
      )}
      <Changes lines={lines} />
    </Window>
  );
}

function UndoWindow({ url, onClose, onDone }: {
  url: string; onClose: () => void; onDone: (text: string) => void;
}) {
  const [p, setP] = useState<UndoPreview | null>(null);
  const [reason, setReason] = useState('Misclick');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<UndoPreview>(url).then(setP).catch((err) => setError(errorText(err, 'Could not open this')));
  }, [url]);

  async function submit() {
    if (!p?.allowed) return;
    setBusy(true); setError('');
    try {
      await api.post(url, { reason: reason.trim() });
      onDone(`${p.what} undone · ${p.reel}`);
    } catch (err) {
      setError(errorText(err, 'Could not undo'));
    } finally {
      setBusy(false);
    }
  }

  const batch = url.includes('/reel-batches/');
  const lines = p?.allowed ? countLines(p.rows, batch) : [];

  return (
    <Window title={p ? `Undo ${p.what}?` : 'Undo'} sub={p?.reel} width={440} error={error} onClose={onClose}
            foot={p && !p.allowed ? (
              <button className="btn primary" onClick={onClose}>Close</button>
            ) : (
              <>
                <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
                <button className="btn primary" disabled={busy || !p} onClick={() => void submit()}>
                  {busy ? 'Saving…' : 'Undo'}
                </button>
              </>
            )}>
      {p && (
        <div className="dim" style={{ fontSize: 13, marginBottom: 10 }}>
          {p.what} · {fmtTimestamp(p.doneAt)} · {p.doneBy}
        </div>
      )}
      {p && !p.allowed && <div style={{ marginBottom: 6 }}>{p.blocked}</div>}
      {p?.allowed && (
        <>
          <Changes lines={lines} />
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="ru-reason">Reason</label>
            <input id="ru-reason" type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </>
      )}
    </Window>
  );
}

function History({ canUpdate, onUndo }: { canUpdate: boolean; onUndo: (entryId: number) => void }) {
  const [view, setView] = useState<'entries' | 'days'>('entries');
  return (
    <>
      <div style={{ display: 'flex', gap: 6, padding: '12px 16px 0' }}>
        {([['entries', 'Entries'], ['days', 'By day']] as const).map(([key, label]) => (
          <button key={key} aria-pressed={view === key} className={`btn${view === key ? ' primary' : ''}`}
                  onClick={() => setView(key)}>
            {label}
          </button>
        ))}
      </div>
      {view === 'entries' ? <Entries canUpdate={canUpdate} onUndo={onUndo} /> : <Days />}
    </>
  );
}

function Days() {
  const [opened, setOpened] = useState<string | null>(null);
  const list = useList<Day>('/api/stock/reel-days', 'days', { sort: 'day', dir: 'desc' });
  const dash = (n: number) => (n ? <strong>{fmt(n)}</strong> : <span className="dim">—</span>);
  const columns: Column<Day>[] = [
    { key: 'day', label: 'Day', type: 'date', render: (d) => <strong>{fmtDate(d.day)}</strong> },
    { key: 'full_at_start', label: 'Full at start', type: 'number', align: 'right', render: (d) => fmt(d.full_at_start) },
    { key: 'half_at_start', label: 'Half at start', type: 'number', align: 'right', render: (d) => fmt(d.half_at_start) },
    { key: 'stock_in', label: 'Stock in', type: 'number', align: 'right', render: (d) => dash(d.stock_in) },
    { key: 'to_half', label: 'Full → Half', type: 'number', align: 'right', render: (d) => dash(d.to_half) },
    { key: 'used_full', label: 'Full used', type: 'number', align: 'right', render: (d) => dash(d.used_full) },
    { key: 'used_half', label: 'Half used', type: 'number', align: 'right', render: (d) => dash(d.used_half) },
    { key: 'count_fixed', label: 'Count fixed', type: 'number', align: 'right', render: (d) => dash(d.count_fixed) },
    { key: 'full_on_hand', label: 'Full at end', type: 'number', align: 'right', render: (d) => fmt(d.full_on_hand) },
    { key: 'half_on_hand', label: 'Half at end', type: 'number', align: 'right', render: (d) => fmt(d.half_on_hand) },
  ];
  return (
    <>
      <DataTable list={list} columns={columns} rowKey={(d) => d.day}
                 onRowClick={(d) => setOpened(String(d.day).slice(0, 10))}
                 exportName="/api/stock/reel-days" empty="Nothing recorded yet." />
      {opened && <DaySnapshot day={opened} onClose={() => setOpened(null)} />}
    </>
  );
}

function DaySnapshot({ day, onClose }: { day: string; onClose: () => void }) {
  const [levels, setLevels] = useState<Level[] | null>(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.get<{ levels: Level[] }>(`/api/stock/reel-snapshot?date=${day}`)
      .then((r) => setLevels(r.levels))
      .catch((err) => setError(errorText(err, 'Could not load')));
  }, [day]);

  const groups = useMemo(() => {
    const map = new Map<string, { key: string; title: string; rows: Level[]; full: number; half: number }>();
    for (const l of levels ?? []) {
      const key = groupKey(l);
      const g = map.get(key) ?? { key, title: groupTitle(l), rows: [], full: 0, half: 0 };
      g.rows.push(l);
      g.full += l.full_reels;
      g.half += l.half_reels;
      map.set(key, g);
    }
    return [...map.values()];
  }, [levels]);

  const full = groups.reduce((n, g) => n + g.full, 0);
  const half = groups.reduce((n, g) => n + g.half, 0);

  const allOpen = groups.length > 0 && groups.every((g) => open.has(g.key));
  const flip = (key: string) => setOpen((o) => {
    const next = new Set(o);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <Window title={`Reels at start of ${fmtDate(day)}`} sub={`${fmt(full)} Full · ${fmt(half)} Half`} width={760}
            error={error} onClose={onClose}
            foot={(
              <>
                <button className="btn" disabled={groups.length === 0}
                        onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map((g) => g.key)))}>
                  {allOpen ? 'Collapse all' : 'Expand all'}
                </button>
                <button className="btn primary" onClick={onClose}>Close</button>
              </>
            )}>
      {levels === null && !error && <div className="dim">Loading…</div>}
      {levels?.length === 0 && <div className="dim">No reels were in stock before this day.</div>}
      {groups.length > 0 && (
        <div className="reel-scroll">
        <table className="reel-grid">
          <colgroup><col /><col style={{ width: 110 }} /><col style={{ width: 110 }} /></colgroup>
          <thead>
            <tr><th>Type / Size</th><th className="num">Full</th><th className="num">Half</th></tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.key}>
                <tr className="reel-type" onClick={() => flip(g.key)}>
                  <td>
                    <span className="reel-twirl">{open.has(g.key) ? '▾' : '▸'}</span>
                    {g.title}
                    <span className="dim" style={{ fontWeight: 400 }}> · {g.rows.length} size{g.rows.length === 1 ? '' : 's'}</span>
                  </td>
                  <td className="num">{fmt(g.full)}</td>
                  <td className="num">{fmt(g.half)}</td>
                </tr>
                {open.has(g.key) && g.rows.map((l) => (
                  <tr key={l.spec_id} className="reel-size">
                    <td>{sizeLabel(l)}</td>
                    <td className="num">{fmt(l.full_reels)}</td>
                    <td className="num">{fmt(l.half_reels)}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="reel-total">
              <td>Total</td><td className="num">{fmt(full)}</td><td className="num">{fmt(half)}</td>
            </tr>
          </tfoot>
        </table>
        </div>
      )}
    </Window>
  );
}

function Entries({ canUpdate, onUndo }: { canUpdate: boolean; onUndo: (entryId: number) => void }) {
  const list = useList<Entry>('/api/stock/reel-entries', 'entries', { sort: 'created_at', dir: 'desc' });
  const columns: Column<Entry>[] = [
    { key: 'created_at', label: 'When', type: 'date',
      render: (e) => <span className="dim" style={{ whiteSpace: 'nowrap' }}>{fmtTimestamp(e.created_at)}</span> },
    { key: 'kind', label: 'Entry', type: 'choice',
      choices: (Object.keys(KIND_LABEL) as Kind[]).map((k) => ({ value: k, label: KIND_LABEL[k] })),
      render: (e) => <strong>{KIND_LABEL[e.kind]}</strong> },
    { key: 'reel_name', label: 'Reel' },
    { key: 'full_change', label: 'Full', type: 'number', align: 'right',
      render: (e) => <strong>{signed(e.full_change)}</strong> },
    { key: 'half_change', label: 'Half', type: 'number', align: 'right',
      render: (e) => <strong>{signed(e.half_change)}</strong> },
    { key: 'job_number', label: 'Job',
      render: (e) => (e.job_number ? <span className="mono">{e.job_number}</span> : <span className="dim">—</span>) },
    { key: 'supplier_name', label: 'Supplier', secondary: true,
      render: (e) => <span className="dim">{e.supplier_name ?? '—'}</span> },
    { key: 'reason', label: 'Reason', secondary: true,
      render: (e) => <span className="dim">{e.reason ?? '—'}</span> },
    { key: 'entered_by_name', label: 'By',
      render: (e) => <span className="dim">{e.entered_by_name}</span> },
    ...(canUpdate ? [{
      key: 'entry_id', label: '', sortable: false, filterable: false,
      render: (e: Entry) => (e.reversed_by_id || e.kind === 'UNDO' ? null : (
        <button className="btn" onClick={() => onUndo(e.entry_id)}>Undo</button>
      )),
    } satisfies Column<Entry>] : []),
  ];
  return (
    <DataTable list={list} columns={columns} rowKey={(e) => e.entry_id}
               rowClass={(e) => (e.reversed_by_id ? 'struck' : '')}
               exportName="/api/stock/reel-entries"
               empty="No reel entries yet." />
  );
}
